import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { CheckCircle, Loader, Copy } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PublicSignup() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [loginUrl, setLoginUrl] = useState('');
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    confirm_password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (formData.password !== formData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setSubmitting(true);

    try {
      const response = await axios.post(`${API}/public-signup`, {
        email: formData.email,
        password: formData.password,
        first_name: formData.first_name,
        last_name: formData.last_name
      });

      // Store token and user with correct keys for client portal
      localStorage.setItem('client_token', response.data.access_token);
      localStorage.setItem('client_user', JSON.stringify(response.data.user));
      axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.access_token}`;

      // Meta Pixel: Track Lead (account created)
      if (window.fbq) {
        window.fbq('track', 'Lead');
      }

      toast.success('Account created! Let\'s get you started.');
      
      // Go straight to onboarding
      navigate('/app/onboarding');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create account');
      setSubmitting(false);
    }
  };

  const copyLoginUrl = () => {
    navigator.clipboard.writeText(loginUrl).then(() => {
      toast.success('Login link copied!');
    }).catch(() => {
      const textArea = document.createElement('textarea');
      textArea.value = loginUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast.success('Login link copied!');
    });
  };

  const goToOnboarding = () => {
    navigate('/app/onboarding');
  };

  if (success) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* MealGuide Branding */}
          <div className="text-center mb-8">
            <img src="/logo.png" alt="MealGuide" className="h-32 mx-auto mb-2" />
            <p className="text-gray-600">Your personalized nutrition journey</p>
          </div>

          <Card className="border-0 shadow-2xl">
            <CardHeader className="space-y-1 pb-6">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-full bg-[#1a61e8]/10 flex items-center justify-center">
                  <CheckCircle className="h-8 w-8 text-[#1a61e8]" />
                </div>
              </div>
              <CardTitle className="text-2xl text-center font-bold text-gray-900">Welcome to MealGuide!</CardTitle>
              <CardDescription className="text-center text-base">
                Hi {formData.first_name}, your account is ready
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
                <p className="text-sm font-semibold text-[#1a61e8] mb-3">📱 Save Your Login Link</p>
                <div className="flex gap-2">
                  <Input
                    value={loginUrl}
                    readOnly
                    className="text-sm bg-white"
                  />
                  <Button
                    onClick={copyLoginUrl}
                    variant="outline"
                    size="sm"
                    className="border-[#1a61e8] text-[#1a61e8] hover:bg-[#1a61e8] hover:text-white"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-xs text-gray-600 mt-2">
                  Save this link! Use it anytime to log in with your email and password.
                </p>
              </div>

              <div className="bg-gradient-to-r from-[#1a61e8]/5 to-cyan-50 border border-[#1a61e8]/20 rounded-xl p-4">
                <p className="text-sm text-gray-700">
                  <strong className="text-[#1a61e8]">Next Step:</strong> Complete your onboarding so we can create your personalized meal plan.
                </p>
              </div>

              <Button 
                onClick={goToOnboarding}
                className="w-full h-12 text-base bg-[#1a61e8] hover:bg-[#1552c7]"
              >
                Continue to Onboarding →
              </Button>

              <p className="text-center text-xs text-gray-500 mt-4">
                Questions? Contact your coach anytime
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Back to homepage link */}
        <a 
          href="/" 
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
          </svg>
          Back to homepage
        </a>
        
        {/* MealGuide Branding */}
        <div className="text-center mb-8">
          <a href="/" className="cursor-pointer hover:opacity-80 transition-opacity inline-block">
            <img src="/logo.png" alt="MealGuide" className="h-32 mx-auto mb-2" />
          </a>
          <p className="text-gray-600">Lose Weight Without Figuring Out What to Eat</p>
        </div>

        <Card className="border-0 shadow-2xl">
          <CardHeader className="space-y-1 pb-6">
            <CardTitle className="text-2xl text-center font-bold text-gray-900">Create Your Account</CardTitle>
            <CardDescription className="text-center text-base">
              After, you'll go through a 2-minute onboarding.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name" className="text-gray-700">First Name</Label>
                  <Input
                    id="first_name"
                    value={formData.first_name}
                    onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                    required
                    placeholder="John"
                    className="h-11"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name" className="text-gray-700">Last Name</Label>
                  <Input
                    id="last_name"
                    value={formData.last_name}
                    onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                    required
                    placeholder="Doe"
                    className="h-11"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-gray-700">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                  placeholder="your@email.com"
                  className="h-11"
                />
                <p className="text-xs text-gray-500">
                 Please, use a real email address. You'll use this email to log in.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-gray-700">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  required
                  placeholder="Minimum 6 characters"
                  className="h-11"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm_password" className="text-gray-700">Confirm Password</Label>
                <Input
                  id="confirm_password"
                  type="password"
                  value={formData.confirm_password}
                  onChange={(e) => setFormData({...formData, confirm_password: e.target.value})}
                  required
                  placeholder="Re-enter your password"
                  className="h-11"
                />
              </div>

              <Button 
                type="submit" 
                className="w-full h-12 text-base bg-[#1a61e8] hover:bg-[#1552c7] mt-6"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <Loader className="mr-2 h-5 w-5 animate-spin" />
                    Creating Your Account...
                  </>
                ) : (
                  'Create Account & Get Started'
                )}
              </Button>

              <div className="text-center pt-4">
                <p className="text-sm text-gray-600">
                  Already have an account?{' '}
                  <button
                    type="button"
                    onClick={() => navigate('/app/login')}
                    className="text-[#1a61e8] hover:underline font-medium"
                  >
                    Sign in
                  </button>
                </p>
              </div>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-gray-500 mt-6">
          By creating an account, you agree to our Terms of Service
        </p>
      </div>
    </div>
  );
}
